import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Mail, KeyRound, ShieldCheck } from "lucide-react";
import { Logo } from "@/components/Logo";
import { AnimatedWaves } from "@/components/AnimatedWaves";

const ADMIN_EMAIL = "admin@costadorada1.com";

/**
 * Stands in for the retired self-registration form at /register.
 *
 * The route is kept (rather than deleted) on purpose: App.tsx has a catch-all that
 * redirects unknown paths to the landing page, so removing it would silently bounce
 * anyone arriving from an old bookmark or emailed link with no explanation.
 */
export function AccessByInvitationPage() {
  const { i18n } = useTranslation();
  const isEn = i18n.language.startsWith("en");
  const tr = (es: string, en: string) => (isEn ? en : es);

  return (
    <div className="min-h-screen flex items-center justify-center bg-hero-gradient text-white px-4 py-10 relative overflow-hidden">
      <div className="absolute inset-0 bg-ocean-mist opacity-60" aria-hidden="true" />
      <AnimatedWaves position="bottom" tone="light" />

      <Link
        to="/"
        className="absolute top-6 left-6 text-sm text-primary-100 hover:text-white tracking-wider uppercase z-10"
      >
        ← {tr("Volver al inicio", "Back home")}
      </Link>

      <div className="w-full max-w-lg relative z-10">
        <div className="text-center mb-6">
          <div className="flex justify-center mb-4">
            <Logo variant="stacked" color="white" className="text-lg" />
          </div>
          <p className="text-primary-100/80 text-sm tracking-wider uppercase">
            {tr("Acceso al portal", "Portal access")}
          </p>
        </div>

        <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-elegant-lg p-8 border border-white/40">
          <div className="flex items-center gap-2 text-dorado-700 mb-2">
            <ShieldCheck size={20} />
            <span className="text-xs uppercase tracking-wider font-medium">
              {tr("Acceso por invitación", "Access by invitation")}
            </span>
          </div>
          <h2 className="font-serif text-2xl text-primary-800 mb-1">
            {tr("El registro público está cerrado", "Public sign-up is closed")}
          </h2>
          <div className="w-10 h-0.5 bg-dorado mb-4" />

          <p className="text-sm text-slate-600 mb-3">
            {tr(
              "Las credenciales del portal las emite la administración del condominio. No es posible crear una cuenta por cuenta propia.",
              "Portal credentials are issued by the condominium administration. Accounts cannot be created on your own."
            )}
          </p>
          <p className="text-sm text-slate-600 mb-6">
            {tr(
              "Si es titular o residente y aún no tiene acceso, comuníquese con la administración y le enviaremos su usuario y contraseña.",
              "If you are an owner or resident and do not have access yet, contact the administration and we will send you your username and password."
            )}
          </p>

          <a
            href={`mailto:${ADMIN_EMAIL}?subject=${encodeURIComponent(
              tr("Solicitud de acceso al portal", "Portal access request")
            )}`}
            className="flex items-center justify-center gap-2 w-full bg-dorado hover:bg-dorado-600 text-white rounded-md px-4 py-3 font-medium transition mb-3"
          >
            <Mail size={18} />
            {tr("Escribir a la administración", "Email the administration")}
          </a>

          <Link
            to="/login"
            className="flex items-center justify-center gap-2 w-full border border-primary-200 text-primary-800 hover:bg-primary-50 rounded-md px-4 py-3 font-medium transition"
          >
            {tr("Ya tengo credenciales — Iniciar sesión", "I have credentials — Sign in")}
          </Link>

          <div className="mt-5 pt-4 border-t border-slate-200 text-center">
            <Link
              to="/forgot-password"
              className="inline-flex items-center gap-1.5 text-sm text-primary-700 hover:text-primary-900 underline underline-offset-2"
            >
              <KeyRound size={14} />
              {tr("¿Olvidó su contraseña?", "Forgot your password?")}
            </Link>
          </div>
        </div>

        <p className="text-center text-xs text-primary-200/70 mt-6 tracking-wider uppercase">
          {tr("Condominio Costa Dorada I", "Costa Dorada I Condominium")}
        </p>
      </div>
    </div>
  );
}
